import React, { useState, useEffect, useCallback, useRef } from 'react';
import './TimeSinceCounter.css';
import IncidentLog from './IncidentLog';
import QuotesOverlay from './QuotesOverlay';
import { v4 as uuidv4 } from 'uuid';
import {
  db,
  collection,
  query,
  orderBy,
  where,
  addDoc,
  updateDoc,
  onSnapshot,
  Timestamp
} from './firebase';

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : date.toDate();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : date.toDate();
    const dateStr = formatDate(d);
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const seconds = d.getSeconds().toString().padStart(2, '0');
    return `${dateStr}, ${hours}:${minutes}:${seconds}`;
  };



function TimeSinceCounter() {
  const [incidents, setIncidents] = useState([]);
  const prevTopIdRef = useRef(null);
  const [quotes, setQuotes] = useState([]);
  const [lastIncidentDate, setLastIncidentDate] = useState(new Date());
  const [timeString, setTimeString] = useState('0:00:00');
  const [showLog, setShowLog] = useState(false);
  const [weeklyCount, setWeeklyCount] = useState(0);
  const [nextResetDate, setNextResetDate] = useState('');
  const [quoteInput, setQuoteInput] = useState('');
  const [showQuoteInput, setShowQuoteInput] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [deviceId] = useState(() => {
    const savedId = localStorage.getItem('deviceId');
    if (savedId) return savedId;
    const newId = uuidv4();
    localStorage.setItem('deviceId', newId);
    return newId;
  });

  const getNextResetDateObject = (fromDate) => {
    const date = new Date(fromDate);
    const day = date.getDay();
    const daysUntilNextMonday = day === 1 ? 7 : (1 + 7 - day) % 7;
    date.setDate(date.getDate() + daysUntilNextMonday);
    date.setHours(0, 0, 0, 0);
    const targetUtcTimestamp = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 6, 0, 0);
    return new Date(targetUtcTimestamp);
  };

  const getStartOfCurrentWeek = useCallback(() => {
    const now = new Date();
    const day = now.getDay();
    const daysSinceMonday = (day === 0) ? 6 : (day - 1);
    const start = new Date(now);
    start.setDate(now.getDate() - daysSinceMonday);
    start.setHours(0, 0, 0, 0);
    const targetUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 6, 0, 0);
    return new Date(targetUtc);
  }, []);

  useEffect(() => {
    if ('Notification' in window) {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);

    const incidentsRef = collection(db, 'incidents');
    const incidentsQuery = query(incidentsRef, orderBy('date', 'desc'));

    const unsubscribeIncidents = onSnapshot(incidentsQuery, snapshot => {
      const fetched = snapshot.docs.map(doc => {
        const data = doc.data();
        const jsDate = data.date?.toDate() ?? new Date();
        return {
          id: doc.id,
          ...data,
          date: jsDate,
          formattedDate: formatDateTime(jsDate),
        };
      });

      const newTopId = fetched[0]?.id;

      if (
        newTopId &&
        prevTopIdRef.current &&
        newTopId !== prevTopIdRef.current &&
        Notification.permission === 'granted'
      ) {
        new Notification('New Incident Recorded', {
          body: `At ${fetched[0].formattedDate}`,
          tag: 'incident-alert'
        });
      }

      prevTopIdRef.current = newTopId;
      setIncidents(fetched);
      setLastIncidentDate(fetched[0]?.date ?? new Date());
      setIsLoading(false);
    });


    const quotesRef = collection(db, 'quotes');
    const quotesQuery = query(quotesRef, orderBy('date', 'desc'));
    const unsubscribeQuotes = onSnapshot(quotesQuery, snapshot => {
      const fetched = snapshot.docs.map(doc => {
        const data = doc.data();
        const jsDate = data.date?.toDate() ?? new Date();
        return { id: doc.id, ...data, date: jsDate };
      });
      setQuotes(fetched);
    });

    const startOfWeek = getStartOfCurrentWeek();
    const weeklyQuery = query(
      incidentsRef,
      where('date', '>=', Timestamp.fromDate(startOfWeek))
    );
    const unsubscribeWeekly = onSnapshot(weeklyQuery, snapshot => {
      setWeeklyCount(snapshot.size);
    });

    const nextResetDt = getNextResetDateObject(new Date());
    setNextResetDate(formatDate(nextResetDt));

    return () => {
      unsubscribeIncidents();
      unsubscribeQuotes();
      unsubscribeWeekly();
    };
  }, [getStartOfCurrentWeek]);

  useEffect(() => {
    const calculateTime = () => {
      const now = new Date();
      const incidentDate = lastIncidentDate instanceof Date ? lastIncidentDate : new Date(lastIncidentDate);
      const timeDiff = now - incidentDate;

      if (timeDiff < 0) {
        setTimeString('0:00:00');
        return;
      }

      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      setTimeString(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);

    return () => clearInterval(interval);
  }, [lastIncidentDate]);

  const getTimeSincePrevious = (currentDate) => {
    if (incidents.length < 1) return null;

    const previousIncident = incidents[0];
    if (!previousIncident?.date) return null;

    const currentDateTime = currentDate instanceof Date ? currentDate : new Date();
    const previousDateTime = previousIncident.date instanceof Date ? previousIncident.date : new Date();

    const timeDiff = currentDateTime - previousDateTime;
    if (timeDiff <= 0) return null;

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    return `${days}d ${hours}h ${minutes}m`;
  };

  const showQuoteForm = () => setShowQuoteInput(true);
  const hideQuoteForm = () => {
    setShowQuoteInput(false);
    setQuoteInput('');
  };

  const resetCounter = async () => {
    const now = new Date();
    const nowTimestamp = Timestamp.fromDate(now);
    const timeSincePrevious = getTimeSincePrevious(now);

    const newIncidentData = {
      deviceId,
      date: nowTimestamp,
      timeSincePrevious,
      quote: quoteInput.trim() || null,
    };

    const optimisticIncident = {
      ...newIncidentData,
      id: `temp-${uuidv4()}`,
      date: now,
      formattedDate: formatDateTime(now),
    };

    setIncidents(prev => [optimisticIncident, ...prev]);
    setLastIncidentDate(now);
    setTimeString('0:00:00');
    hideQuoteForm();

    try {
      const incidentRef = await addDoc(collection(db, 'incidents'), newIncidentData);
      await updateDoc(incidentRef, { lastIncidentDate: nowTimestamp });

      setIncidents(prev =>
        prev.map(inc =>
          inc.id === optimisticIncident.id ? { ...inc, id: incidentRef.id } : inc
        )
      );

      if (quoteInput.trim()) {
        const newQuoteData = {
          text: quoteInput.trim(),
          date: nowTimestamp,
        };
        const quoteRef = await addDoc(collection(db, 'quotes'), newQuoteData);
        setQuotes(prev => [{ ...newQuoteData, id: quoteRef.id, date: now }, ...prev]);
      }
    } catch (err) {
      console.error("Error saving data to Firestore: ", err);
      alert("Failed to save incident. Please check console for errors.");
    } finally {
      setQuoteInput('');
    }
  };

  const toggleLog = () => setShowLog(!showLog);

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="counter-container">
      <QuotesOverlay quotes={quotes} />
      <div className="counter-card">
        <h1 className="counter-title">
          Time Since Minseo Verbally Abused Harry
        </h1>

        <div className="time-display">{timeString}</div>
        <div className="time-labels">HOURS : MINUTES : SECONDS</div>

        <div className="weekly-counter">
          <div className="weekly-count">{weeklyCount}</div>
          <div className="weekly-label">incidents this week</div>
          <div className="reset-info">Resets on Monday 12:00 AM CT</div>
          <div className="next-reset">Next reset: {nextResetDate}</div>
        </div>

        {!showQuoteInput ? (
          <div className="button-container">
            <button onClick={showQuoteForm} className="reset-button">
              Record New Incident
            </button>
            <button onClick={toggleLog} className="reset-button">
              {showLog ? "Hide Incident Log" : "Show Incident Log"}
            </button>
          </div>
        ) : (
          <div className="quote-form">
            <textarea
              value={quoteInput}
              onChange={(e) => setQuoteInput(e.target.value)}
              placeholder="What did Minseo say? (Optional)"
              className="quote-input"
              rows="3"
            />
            <div className="quote-buttons">
              <button onClick={hideQuoteForm} className="cancel-button">
                Cancel
              </button>
              <button onClick={resetCounter} className="submit-button">
                Submit Incident
              </button>
            </div>
          </div>
        )}

        <div className="incident-date">
          Last incident: {incidents.length > 0 ? incidents[0].formattedDate : "No incidents recorded"}
        </div>

        {showLog && <IncidentLog incidents={incidents} />}
      </div>
    </div>
  );
}

export default TimeSinceCounter;

